/**
 * Created: 2026-06-12
 * Initial commit: Refactor user selector into standalone component (feat/manage/mr-5)
 *
 * Controlled autocomplete for searching and selecting a site member by name or mobile.
 */

import { gql } from '@apollo/client'
import { useLazyQuery } from '@apollo/client/react'
import { Autocomplete, type SxProps, TextField, type Theme } from '@mui/material'
import { useState } from 'react'
import { type Gender, UsersDocument } from '../gql/graphql'
import UserAvatar from './UserAvatar'

// Created: 2026-06-12 | Refactor user selector into standalone component
gql`
  query Users($userFilter: UserFilterInput!) {
    users(userFilter: $userFilter) {
      id
      openid
      name
      avatar
      mobile
      gender
    }
  }
`

export type UserSelectorUser = {
  id: string
  name: string | null | undefined
  avatar: string | null | undefined
  mobile: string | null | undefined
  gender: Gender | null | undefined
}

interface UserSelectorProps {
  siteId: string
  value: UserSelectorUser | null
  onChange: (user: UserSelectorUser | null) => void
  onClear?: () => void
  sx?: SxProps<Theme>
}

export default function UserSelector({
  siteId,
  value,
  onChange,
  onClear,
  sx,
}: UserSelectorProps) {
  const [inputValue, setInputValue] = useState('')
  const [options, setOptions] = useState<UserSelectorUser[]>([])

  const [findUsers] = useLazyQuery(UsersDocument, { fetchPolicy: 'network-only' })

  const canSearch = inputValue.trim().length >= 1

  const handleSearchUsers = async () => {
    if (!canSearch) return
    const { data } = await findUsers({
      variables: { userFilter: { siteId, query: inputValue.trim() } },
    })
    const found = (data?.users ?? []).map((u) => ({
      id: u.id ?? '',
      name: u.name,
      avatar: u.avatar,
      mobile: u.mobile,
      gender: u.gender,
    }))
    setOptions(found)
  }

  return (
    <Autocomplete
      sx={[{ maxWidth: 250, flex: 1 }, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
      options={options}
      getOptionLabel={(u) => `${u.name ?? ''} ${u.mobile ?? ''}`.trim()}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      value={value}
      inputValue={inputValue}
      clearOnBlur={false}
      onInputChange={(_, val) => {
        setInputValue(val)
        if (val === '') {
          setOptions([])
          onChange(null)
          onClear?.()
        }
      }}
      onChange={(_, val) => {
        setOptions([])
        onChange(val)
      }}
      filterOptions={(x) => x}
      noOptionsText="未找到该用户"
      renderInput={(params) => (
        <TextField
          {...params}
          label="手机号或姓名"
          size="small"
          slotProps={{ inputLabel: { shrink: true } }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && canSearch) handleSearchUsers()
          }}
        />
      )}
      renderOption={(props, u) => (
        <li {...props} key={u.id}>
          <UserAvatar user={u} />
        </li>
      )}
    />
  )
}
